import { inject, Pipe, PipeTransform } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { format, formatRelative, Locale } from 'date-fns';
import { enGB, fr, nl } from 'date-fns/locale';

interface RelativeDate {
  lastWeek: string;
  yesterday: string;
  today: string;
  tomorrow: string;
  nextWeek: string;
  other: string;
}

const LOCALES: Record<string, Locale> = { '1': nl, '2': fr, '3': enGB };

/**
 * Formats the date to a localized one. By default it will show the date in textual format until six days in the past or future.
 * E.g. if it is today, it will show 'today'. If it is tomorrow, it will show 'tomorrow'. If it is 3 days ago and today is monday, it will show 'past friday'.
 * Any other day will default be formatted in dd/MM/yyyy.
 *
 * The relative format can be switch off by setting isRelativeFormat to false.
 */
@Pipe({
  name: 'formatLocalizedDate',
  standalone: false,
})
export class FormatLocalizedDatePipe implements PipeTransform {
  private readonly translateService = inject(TranslateService);
  transform(
    date: Date,
    otherDateFormat = 'dd/MM/yyyy',
    isTimeIncluded = false,
    isRelativeFormat = true,
  ): string {
    let [, languageId] = this.translateService.currentLang.split('_');
    if (!languageId) languageId = '1';
    const localeWithoutTime = {
      ...LOCALES[languageId],
      formatRelative: (token: string): string => {
        return formatRelativeLocale(otherDateFormat)[languageId][token];
      },
    };
    let options: {
      locale?: Locale;
      weekStartsOn?: 0 | 1 | 2 | 3 | 4 | 5 | 6;
    } = isTimeIncluded
      ? { locale: LOCALES[languageId] }
      : { locale: localeWithoutTime };
    options = { ...options, weekStartsOn: 1 };
    return isRelativeFormat
      ? formatRelative(date, new Date(), options)
      : format(date, otherDateFormat, options);
  }
}

function formatRelativeLocaleEnGB(format: string): RelativeDate {
  return {
    lastWeek: "'last' eeee",
    yesterday: "'yesterday'",
    today: "'today'",
    tomorrow: "'tomorrow'",
    nextWeek: "'next' eeee",
    other: format,
  };
}

function formatRelativeLocaleNl(format: string): RelativeDate {
  return {
    lastWeek: "'afgelopen' eeee",
    yesterday: "'gisteren'",
    today: "'vandaag'",
    tomorrow: "'morgen'",
    nextWeek: "'volgende' eeee",
    other: format,
  };
}

function formatRelativeLocaleFr(format: string): RelativeDate {
  return {
    lastWeek: "eeee 'dernier'",
    yesterday: "'hier'",
    today: "'aujourd''hui'",
    tomorrow: "'demain'",
    nextWeek: "eeee 'prochain'",
    other: format,
  };
}

function formatRelativeLocale(format: string): Record<string, any> {
  return {
    '1': formatRelativeLocaleNl(format),
    '2': formatRelativeLocaleFr(format),
    '3': formatRelativeLocaleEnGB(format),
  };
}
